import React, { Suspense } from "react";
import { baseUrl, getComponentFromPath } from "@kaltura/mediaspace-shared-utils";
import { KmsTypeComponent, KmsTypeHeroProps } from "@kaltura/mediaspace-shared-types";
import { ErrorBoundary } from "@kaltura/mediaspace-shared-ui";
import { ModuleDataSkeleton } from "./skeleton/ModuleDataSkeleton";
import styled from "@emotion/styled";
import { useFetchKmsData } from "@kaltura/mediaspace-shared-hooks";

interface MediaModuleDataProps {
    fetchModuleData: () => { components: KmsTypeComponent[] };
}

const StyledModuleDataContainer = styled("div")(({ theme }) => ({
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(4),
    paddingTop: theme.spacing(4),
}));

/**
 * Media module data - attachments, comments, etc.
 */
function MediaModuleData({ fetchModuleData }: MediaModuleDataProps) {
    const { components } = fetchModuleData();

    const className = "kms-ds-media-page-moduledata";

    return (
        <StyledModuleDataContainer className={className}>
            {components.map(({ component, props }, index) => {
                const Component = getComponentFromPath(component);
                return (
                    <ErrorBoundary key={index} fallback={<></>}>
                        <div className={className + "-item"}>
                            <Component {...props} />
                        </div>
                    </ErrorBoundary>
                );
            })}
        </StyledModuleDataContainer>
    );
}

export interface MediaModuleDataContainerProps extends Pick<KmsTypeHeroProps, "categoryId" | "playlist"> {
    entryId: string;
    reloadCounter?: number;
    isReadOnly?: boolean;
}

/**
 * Media module data suspense container
 */
export function MediaModuleDataContainer({
    entryId,
    categoryId,
    playlist,
    reloadCounter = 0,
    isReadOnly = false,
}: MediaModuleDataContainerProps) {
    // fetch data from kms
    const url =
        baseUrl
        + "/default/media/module-data/id/"
        + entryId
        + (categoryId ? "/categoryid/" + categoryId : "")
        + (playlist ? "/playlistid/" + playlist.id : "");
    const fetchModuleData = useFetchKmsData<{ components: KmsTypeComponent[] }>(
        {
            url,
            data: { isReadOnly },
        },
        [url, reloadCounter]
    );

    // render with Suspense
    return (
        <ErrorBoundary fallback={<ModuleDataSkeleton />}>
            <Suspense fallback={<ModuleDataSkeleton />}>
                <MediaModuleData fetchModuleData={fetchModuleData} />
            </Suspense>
        </ErrorBoundary>
    );
}

export default MediaModuleDataContainer;
